import React from "react";

import { SearchableDropdown } from "../../../../components/ui";
import ProjectSectionTitle from "../ProjectSectionTitle";
import ProjectMetricCard from "./ProjectMetricCard";
import {
  OVERVIEW_METRICS_RANGE_ALL,
  OVERVIEW_METRICS_TYPE_ALL,
} from "./metricsHelpers";

/**
 * Render a full-width overview summary card with metric tiles and a chart.
 *
 * @param {Object} props - Component props.
 * @param {string} props.title - Card title.
 * @param {string} props.subtitle - Card subtitle.
 * @param {Array<Object>} props.metrics - Summary metric tiles.
 * @param {React.ReactNode} props.chart - Chart body for the card.
 * @param {string} [props.error] - Optional error message under the metrics.
 * @param {string} props.typeKey - Current Type selection value.
 * @param {(key: string) => void} props.onTypeChange - Type selection handler.
 * @param {Array<{value: string, label: string}>} props.typeOptions - Type choices.
 * @param {string} props.typeAriaLabel - Accessible label for the type select.
 * @param {string} props.rangeKey - Current All/month selection key.
 * @param {(key: string) => void} props.onRangeChange - Range selection handler.
 * @param {Array<{value: string, label: string}>} props.rangeOptions - Range choices (All + months).
 * @param {string} props.rangeAriaLabel - Accessible label for the range select.
 * @returns {JSX.Element}
 */
const ProjectSummaryCard = ({
  title,
  subtitle,
  metrics,
  chart,
  error,
  typeKey,
  onTypeChange,
  typeOptions,
  typeAriaLabel,
  rangeKey,
  onRangeChange,
  rangeOptions,
  rangeAriaLabel,
}) => {
  return (
    <section className="rounded-xl border border-gray-200 bg-white px-4 py-2 shadow-sm shadow-primary-900/5">
      <div className="mb-2 flex items-start justify-between gap-4">
        <div className="min-w-0">
          <ProjectSectionTitle>{title}</ProjectSectionTitle>
          <p className="mt-1 text-sm text-gray-500">{subtitle}</p>
        </div>
        <div className="flex shrink-0 items-end gap-2">
          <SearchableDropdown
            label="Type"
            value={typeKey}
            onChange={(next) => onTypeChange(next ?? OVERVIEW_METRICS_TYPE_ALL)}
            options={typeOptions}
            ariaLabel={typeAriaLabel}
            clearable={false}
            searchWhenOverflow
            className="min-w-[9rem]"
            inputClassName="rounded-md px-2.5 py-1.5 pr-9"
          />
          <SearchableDropdown
            label="Range"
            value={rangeKey}
            onChange={(next) => onRangeChange(next ?? OVERVIEW_METRICS_RANGE_ALL)}
            options={rangeOptions}
            ariaLabel={rangeAriaLabel}
            clearable={false}
            searchWhenOverflow
            className="min-w-[9rem]"
            inputClassName="rounded-md px-2.5 py-1.5 pr-9"
          />
        </div>
      </div>

      <div
        className={`mt-5 grid grid-cols-2 gap-2 ${
          metrics.length >= 5
            ? "min-[560px]:grid-cols-5"
            : "min-[420px]:grid-cols-4"
        }`}
      >
        {metrics.map((metric) => (
          <ProjectMetricCard
            key={metric.label}
            value={metric.value}
            label={metric.label}
            accent={metric.accent}
          />
        ))}
      </div>

      {error ? (
        <p className="mt-2 text-sm text-red-600">{error}</p>
      ) : null}

      {chart}
    </section>
  );
};

export default ProjectSummaryCard;
